import { useEffect, useState, useMemo } from 'react';
import {
  listarR4020, obterR4020, enviarR4020Req, rodarCrossmatch, obterClienteApi,
  criarR4020, listarNaturezas, calcularRet,
  listarR4010, obterR4010, enviarR4010Req,
} from '../api';
import { PageHeader, Loading, Empty, StatusBadge, Alert, KPI } from '../components/common';
import { fmtMoeda, fmtData, mesAtual, anosOpcoes, mesOpcoes, fmtCnpj, fmtCpf } from '../lib/format';

export default function Retencoes({ clienteId }) {
  const comp = mesAtual();
  const [ano, mes] = comp.split('-').map(Number);
  const [filtroAno, setFiltroAno] = useState(ano);
  const [filtroMes, setFiltroMes] = useState(mes);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detalhe, setDetalhe] = useState(null);
  const [mensagem, setMensagem] = useState(null);
  const [cnpjCliente, setCnpjCliente] = useState('');
  const [modalNovo, setModalNovo] = useState(false);
  const [aba, setAba] = useState('r4020');   // 'r4020' (PJ) | 'r4010' (PF / lucros)

  useEffect(() => {
    if (clienteId) obterClienteApi(clienteId).then(c => setCnpjCliente(c?.nr_insc || ''));
  }, [clienteId]);

  const carregar = () => {
    if (!clienteId) return;
    setLoading(true);
    const filtros = { per_apur: `${filtroAno}-${String(filtroMes).padStart(2, '0')}` };
    if (filtroStatus) filtros.status = filtroStatus;
    listarR4020(clienteId, filtros)
      .then(r => setLista(r.items || []))
      .catch(err => setMensagem({ tipo: 'danger', txt: err.message }))
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [clienteId, filtroAno, filtroMes, filtroStatus]);

  const abrirDetalhe = async (id) => {
    try {
      const r = await obterR4020(clienteId, id);
      setDetalhe(r);
    } catch (err) {
      setMensagem({ tipo: 'danger', txt: err.message });
    }
  };

  const enviar = async (id) => {
    if (!confirm('Enviar este R-4020 para a Receita Federal?')) return;
    try {
      const r = await enviarR4020Req(id);
      setMensagem({ tipo: r.status === 'recebido_async' ? 'success' : 'warning',
        txt: `Status: ${r.status}${r.protocolo ? ' · protocolo: ' + r.protocolo : ''}${r.erro ? ' · ' + r.erro : ''}` });
      carregar();
    } catch (err) {
      setMensagem({ tipo: 'danger', txt: err.message });
    }
  };

  const rodarWorker = async () => {
    if (!cnpjCliente) return setMensagem({ tipo: 'danger', txt: 'CNPJ do cliente não disponível' });
    setMensagem({ tipo: 'info', txt: 'Rodando crossmatch Finance↔Sieg…' });
    try {
      const r = await rodarCrossmatch(clienteId, `${filtroAno}-${String(filtroMes).padStart(2, '0')}`, cnpjCliente);
      setMensagem({ tipo: 'success', txt: `Worker concluído: ${r.criados} R-4020(s) criado(s) · ${r.pendentes} nota(s) sem match` });
      carregar();
    } catch (err) {
      setMensagem({ tipo: 'danger', txt: err.message });
    }
  };

  if (!clienteId) return <Empty title="Selecione um cliente" />;

  const rascunhos  = lista.filter(r => r.status === 'rascunho' || r.status === 'revisao').length;
  const enviados   = lista.filter(r => ['enviado', 'recebido_async', 'processando'].includes(r.status)).length;
  const aceitos    = lista.filter(r => r.status === 'aceito').length;
  const rejeitados = lista.filter(r => r.status === 'rejeitado').length;

  return (
    <>
      <PageHeader
        title="Retenções (EFD-Reinf)"
        sub={aba === 'r4020'
          ? 'R-4020 · Pagamentos a PJ com retenção de IR/CSLL/COFINS/PIS'
          : 'R-4010 · Pagamentos a PF — lucros/dividendos tributados e demais rendimentos'}
        actions={aba === 'r4020' ? (
          <>
            <button onClick={() => setModalNovo(true)}>+ R-4020 manual</button>
            <button onClick={rodarWorker}>↻ Rodar crossmatch Finance↔Sieg</button>
          </>
        ) : null}
      />

      <div className="toolbar" style={{ gap: 8, marginBottom: 4 }}>
        <button
          className={aba === 'r4020' ? 'primary' : 'ghost'}
          onClick={() => { setAba('r4020'); setDetalhe(null); setMensagem(null); }}
        >R-4020 · PJ</button>
        <button
          className={aba === 'r4010' ? 'primary' : 'ghost'}
          onClick={() => { setAba('r4010'); setDetalhe(null); setMensagem(null); }}
        >R-4010 · PF (lucros/dividendos)</button>
      </div>

      {modalNovo && aba === 'r4020' && (
        <ModalR4020Manual
          clienteId={clienteId}
          cnpjCliente={cnpjCliente}
          perApurDefault={`${filtroAno}-${String(filtroMes).padStart(2, '0')}`}
          onFechar={() => setModalNovo(false)}
          onCriado={(r) => {
            setModalNovo(false);
            setMensagem({ tipo: 'success', txt: `R-4020 #${r.id} criado como rascunho` });
            carregar();
          }}
        />
      )}

      {mensagem && <Alert tipo={mensagem.tipo}>{mensagem.txt}</Alert>}

      {aba === 'r4010' && <Retencoes4010 clienteId={clienteId} onMsg={setMensagem} />}

      {aba === 'r4020' && (<>
      <div className="kpi-grid">
        <KPI label="Rascunhos"  value={rascunhos}  variant={rascunhos > 0 ? 'warning' : ''} sub="aguardando aprovação" />
        <KPI label="Enviados"   value={enviados}   variant="" sub="aguardando Receita processar" />
        <KPI label="Aceitos"    value={aceitos}    variant={aceitos > 0 ? 'success' : ''} />
        <KPI label="Rejeitados" value={rejeitados} variant={rejeitados > 0 ? 'danger' : ''} />
      </div>

      <div className="toolbar">
        <select value={filtroAno} onChange={e => setFiltroAno(Number(e.target.value))}>
          {anosOpcoes(5).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filtroMes} onChange={e => setFiltroMes(Number(e.target.value))}>
          {mesOpcoes().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="rascunho">Rascunho</option>
          <option value="revisao">Em revisão</option>
          <option value="recebido_async">Recebido (async)</option>
          <option value="aceito">Aceito</option>
          <option value="rejeitado">Rejeitado</option>
        </select>
      </div>

      <div className="card">
        {loading ? <Loading /> : lista.length === 0 ? (
          <Empty title="Sem R-4020 no período" sub="Use o worker pra gerar rascunhos a partir de Finance↔Sieg" />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Período</th>
                <th>Estabelecimento</th>
                <th>Origem</th>
                <th>Status</th>
                <th>Protocolo / Recibo</th>
                <th>Criado em</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {lista.map(r => (
                <tr key={r.id}>
                  <td>{r.per_apur}</td>
                  <td>{fmtCnpj(r.estab_nr_insc)}</td>
                  <td><span className="badge badge--neutral">{r.origem_tipo || 'manual'}</span></td>
                  <td><StatusBadge status={r.status} /></td>
                  <td className="small">{r.nr_recibo || '—'}</td>
                  <td>{fmtData(r.criado_em)}</td>
                  <td className="flex">
                    <button className="ghost" onClick={() => abrirDetalhe(r.id)}>Ver</button>
                    {(r.status === 'rascunho' || r.status === 'revisao') && (
                      <button className="primary" onClick={() => enviar(r.id)}>Enviar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detalhe && <DetalheR4020 r4020={detalhe} onClose={() => setDetalhe(null)} />}
      </>)}
    </>
  );
}

// =============================================================================
// Aba R-4010 — Retenções em pagamentos a PF (lucros/dividendos tributados)
// =============================================================================
function Retencoes4010({ clienteId, onMsg }) {
  const comp = mesAtual();
  const [ano, mes] = comp.split('-').map(Number);
  const [filtroAno, setFiltroAno] = useState(ano);
  const [filtroMes, setFiltroMes] = useState(mes);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detalhe, setDetalhe] = useState(null);

  const carregar = () => {
    setLoading(true);
    const filtros = { per_apur: `${filtroAno}-${String(filtroMes).padStart(2, '0')}` };
    if (filtroStatus) filtros.status = filtroStatus;
    listarR4010(clienteId, filtros)
      .then(r => setLista(r.items || []))
      .catch(err => onMsg?.({ tipo: 'danger', txt: err.message }))
      .finally(() => setLoading(false));
  };
  useEffect(carregar, [clienteId, filtroAno, filtroMes, filtroStatus]);

  const abrirDetalhe = async (id) => {
    try { setDetalhe(await obterR4010(clienteId, id)); }
    catch (err) { onMsg?.({ tipo: 'danger', txt: err.message }); }
  };

  const enviar = async (id) => {
    if (!confirm('Enviar este R-4010 (lucros/dividendos PF) para a Receita Federal?')) return;
    try {
      const r = await enviarR4010Req(id);
      onMsg?.({ tipo: r.status === 'recebido_async' ? 'success' : 'warning',
        txt: `Status: ${r.status}${r.protocolo ? ' · protocolo: ' + r.protocolo : ''}${r.erro ? ' · ' + r.erro : ''}` });
      carregar();
    } catch (err) { onMsg?.({ tipo: 'danger', txt: err.message }); }
  };

  const rascunhos  = lista.filter(r => r.status === 'rascunho' || r.status === 'revisao').length;
  const enviados   = lista.filter(r => ['enviado', 'recebido_async', 'processando'].includes(r.status)).length;
  const aceitos    = lista.filter(r => r.status === 'aceito').length;
  const totalIRRF  = lista.reduce((s, r) => s + Number(r.vlr_ir_total || 0), 0);

  return (
    <>
      <div className="kpi-grid">
        <KPI label="Rascunhos" value={rascunhos} variant={rascunhos > 0 ? 'warning' : ''} sub="aguardando envio" />
        <KPI label="Enviados"  value={enviados} sub="aguardando Receita" />
        <KPI label="Aceitos"   value={aceitos} variant={aceitos > 0 ? 'success' : ''} />
        <KPI label="IRRF total (10%)" value={fmtMoeda(totalIRRF)} variant={totalIRRF > 0 ? 'danger' : ''} sub="lucros/dividendos" />
      </div>

      <div className="toolbar">
        <select value={filtroAno} onChange={e => setFiltroAno(Number(e.target.value))}>
          {anosOpcoes(5).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filtroMes} onChange={e => setFiltroMes(Number(e.target.value))}>
          {mesOpcoes().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="rascunho">Rascunho</option>
          <option value="revisao">Em revisão</option>
          <option value="recebido_async">Recebido (async)</option>
          <option value="aceito">Aceito</option>
          <option value="rejeitado">Rejeitado</option>
        </select>
      </div>

      <div className="card">
        {loading ? <Loading /> : lista.length === 0 ? (
          <Empty title="Sem R-4010 no período"
                 sub="Gerados automaticamente a partir de lançamentos de distribuição de lucros (>R$50k/mês)" />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Período</th>
                <th>Beneficiário (PF)</th>
                <th>CPF</th>
                <th className="num">Rend. bruto</th>
                <th className="num">IRRF (10%)</th>
                <th>Status</th>
                <th>Recibo</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {lista.map(r => (
                <tr key={r.id}>
                  <td>{r.per_apur}</td>
                  <td>{r.nm_benef}</td>
                  <td className="small mono">{fmtCpf(r.cpf_benef)}</td>
                  <td className="num">{fmtMoeda(r.vlr_rend_bruto_total)}</td>
                  <td className="num bold" style={{ color: 'var(--gold)' }}>{fmtMoeda(r.vlr_ir_total)}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td className="small">{r.nr_recibo || '—'}</td>
                  <td className="flex">
                    <button className="ghost" onClick={() => abrirDetalhe(r.id)}>Ver</button>
                    {(r.status === 'rascunho' || r.status === 'revisao') && (
                      <button className="primary" onClick={() => enviar(r.id)}>Enviar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detalhe && <DetalheR4010 r4010={detalhe} onClose={() => setDetalhe(null)} />}
    </>
  );
}

function DetalheR4010({ r4010, onClose }) {
  const pgtos = r4010.pagamentos || [];
  const totBruto = pgtos.reduce((s, p) => s + Number(p.vlr_rend_bruto || 0), 0);
  const totIR    = pgtos.reduce((s, p) => s + Number(p.vlr_ir || 0), 0);
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(8,8,10,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
    }} onClick={onClose}>
      <div className="card" style={{ width: 640, maxHeight: '85vh', overflow: 'auto', padding: 20 }} onClick={e => e.stopPropagation()}>
        <div className="flex mb-2">
          <h2 style={{ margin: 0, fontSize: 16, color: 'var(--gold)' }}>R-4010 #{r4010.id}</h2>
          <div style={{ flex: 1 }} />
          <button onClick={onClose}>Fechar</button>
        </div>
        <div className="flex small muted" style={{ gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
          <span>Período: <b>{r4010.per_apur}</b></span>
          <span>Beneficiário: <b>{r4010.nm_benef}</b> ({fmtCpf(r4010.cpf_benef)})</span>
          <span>Status: <StatusBadge status={r4010.status} /></span>
        </div>

        <table className="data" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Nat. rend.</th><th>Data FG</th>
              <th className="num">Rend. bruto</th><th className="num">Rend. tributável</th><th className="num">IRRF</th>
            </tr>
          </thead>
          <tbody>
            {pgtos.map(p => (
              <tr key={p.id}>
                <td className="mono">{p.nat_rend}{p.nat_rend === '12001' ? ' (dividendos)' : ''}</td>
                <td>{fmtData(p.dt_fg)}</td>
                <td className="num">{fmtMoeda(p.vlr_rend_bruto)}</td>
                <td className="num">{fmtMoeda(p.vlr_rend_trib)}</td>
                <td className="num bold" style={{ color: 'var(--gold)' }}>{fmtMoeda(p.vlr_ir)}</td>
              </tr>
            ))}
            <tr style={{ background: 'var(--gold-pale)' }}>
              <td className="bold" colSpan={2}>Total</td>
              <td className="num bold">{fmtMoeda(totBruto)}</td>
              <td />
              <td className="num bold" style={{ color: 'var(--gold)' }}>{fmtMoeda(totIR)}</td>
            </tr>
          </tbody>
        </table>

        <p className="small muted" style={{ marginTop: 12 }}>
          Lei 15.270/2025 — distribuição de lucros &gt; R$ 50.000/mês: IRRF 10% (DARF 1841-01). Evento EFD-Reinf evtRetPF.
        </p>
      </div>
    </div>
  );
}

function DetalheR4020({ r4020, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
    }} onClick={onClose}>
      <div className="card" style={{ width: 720, maxHeight: '85vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="flex mb-2">
          <h2 style={{ margin: 0, fontSize: 16 }}>R-4020 #{r4020.id}</h2>
          <div className="toolbar__spacer" />
          <button onClick={onClose}>Fechar</button>
        </div>

        <div className="flex" style={{ gap: 24, fontSize: 12 }}>
          <span>Período: <b>{r4020.per_apur}</b></span>
          <span>Status: <StatusBadge status={r4020.status} /></span>
          <span>Origem: {r4020.origem_tipo}</span>
        </div>

        {(r4020.beneficiarios || []).map(b => (
          <div key={b.id} className="mt-2" style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
            <div className="bold">{b.nm_benef || fmtCnpj(b.cnpj_benef)}</div>
            <div className="small muted">{fmtCnpj(b.cnpj_benef)}</div>

            {(b.pagamentos || []).map(p => (
              <div key={p.id} className="mt-1" style={{ paddingLeft: 12, borderLeft: '2px solid var(--border-accent)' }}>
                <div className="flex">
                  <span className="badge badge--info">natRend: {p.nat_rend}</span>
                  {p.observ && <span className="small muted">{p.observ}</span>}
                </div>

                {(p.infoPgtos || []).map(i => (
                  <table key={i.id} className="data mt-1" style={{ fontSize: 11 }}>
                    <tbody>
                      <tr><td>Data FG</td><td>{fmtData(i.dt_fg)}</td>
                          <td>Valor bruto</td><td className="num bold">{fmtMoeda(i.vlr_bruto)}</td></tr>
                      <tr><td>IR</td><td className="num">{fmtMoeda(i.vlr_ir)}</td>
                          <td>CSLL</td><td className="num">{fmtMoeda(i.vlr_csll)}</td></tr>
                      <tr><td>COFINS</td><td className="num">{fmtMoeda(i.vlr_cofins)}</td>
                          <td>PIS</td><td className="num">{fmtMoeda(i.vlr_pp)}</td></tr>
                      <tr><td>Agregado</td><td className="num">{fmtMoeda(i.vlr_agreg)}</td>
                          <td></td><td></td></tr>
                    </tbody>
                  </table>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Modal: R-4020 manual (pagamento a PJ com retenções)
// =============================================================================
function ModalR4020Manual({ clienteId, cnpjCliente, perApurDefault, onFechar, onCriado }) {
  const hoje = new Date().toISOString().slice(0, 10);
  const [perApur, setPerApur] = useState(perApurDefault);
  const [estabNrInsc, setEstabNrInsc] = useState(cnpjCliente || '');
  const [cnpjBenef, setCnpjBenef] = useState('');
  const [nmBenef, setNmBenef] = useState('');
  const [natRend, setNatRend] = useState('');
  const [observ, setObserv] = useState('');
  const [dtFg, setDtFg] = useState(hoje);
  const [vlrBruto, setVlrBruto] = useState('');
  const [retencoes, setRetencoes] = useState({
    vlr_ir: '', vlr_csll: '', vlr_cofins: '', vlr_pp: '', vlr_agreg: '',
  });
  const [naturezas, setNaturezas] = useState([]);
  const [filtroNat, setFiltroNat] = useState('');
  const [calculando, setCalculando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    listarNaturezas()
      .then(r => setNaturezas(r.items || []))
      .catch(e => setErro('Falha ao carregar naturezas: ' + e.message));
  }, []);

  useEffect(() => { setEstabNrInsc(cnpjCliente || ''); }, [cnpjCliente]);

  const naturezaSelecionada = useMemo(
    () => naturezas.find(n => n.cod === natRend),
    [naturezas, natRend]
  );

  const naturezasFiltradas = useMemo(() => {
    if (!filtroNat) return naturezas.slice(0, 200);
    const f = filtroNat.toLowerCase();
    return naturezas.filter(n =>
      n.cod.includes(f) || (n.descricao || '').toLowerCase().includes(f)
    ).slice(0, 200);
  }, [naturezas, filtroNat]);

  const calcularAutomatico = async () => {
    if (!natRend || !vlrBruto) return setErro('Natureza e valor bruto obrigatórios para cálculo');
    setCalculando(true);
    setErro(null);
    try {
      const r = await calcularRet({
        natRend,
        vlrBruto: Number(vlrBruto),
        referencia: dtFg,
      });
      setRetencoes({
        vlr_ir:     r.vlr_ir     ? String(r.vlr_ir)     : '',
        vlr_csll:   r.vlr_csll   ? String(r.vlr_csll)   : '',
        vlr_cofins: r.vlr_cofins ? String(r.vlr_cofins) : '',
        vlr_pp:     r.vlr_pp     ? String(r.vlr_pp)     : '',
        vlr_agreg:  r.vlr_agreg  ? String(r.vlr_agreg)  : '',
      });
    } catch (err) {
      setErro('Falha no cálculo: ' + err.message);
    } finally {
      setCalculando(false);
    }
  };

  const salvar = async () => {
    setErro(null);
    if (!perApur || !/^\d{4}-\d{2}$/.test(perApur)) return setErro('Período de apuração inválido (AAAA-MM)');
    if (!estabNrInsc || estabNrInsc.replace(/\D/g, '').length !== 14)
      return setErro('CNPJ do estabelecimento (14 dígitos) obrigatório');
    if (!cnpjBenef && !nmBenef) return setErro('CNPJ ou nome do beneficiário obrigatório');
    if (cnpjBenef && cnpjBenef.replace(/\D/g, '').length !== 14)
      return setErro('CNPJ do beneficiário deve ter 14 dígitos');
    if (!natRend || natRend.length !== 5) return setErro('Natureza de rendimento (5 dígitos) obrigatória');
    if (!vlrBruto || Number(vlrBruto) <= 0) return setErro('Valor bruto obrigatório');

    const infoPgto = {
      dt_fg: dtFg,
      vlr_bruto: Number(vlrBruto),
    };
    // Inclui apenas retenções com valor > 0
    for (const [k, v] of Object.entries(retencoes)) {
      if (v && Number(v) > 0) infoPgto[k] = Number(v);
    }

    setSalvando(true);
    try {
      const r = await criarR4020(clienteId, {
        per_apur: perApur,
        ind_retif: 1,
        estab_nr_insc: estabNrInsc.replace(/\D/g, ''),
        origem_tipo: 'manual',
        beneficiarios: [{
          cnpj_benef: cnpjBenef ? cnpjBenef.replace(/\D/g, '') : undefined,
          nm_benef:   nmBenef || undefined,
          pagamentos: [{
            nat_rend: natRend,
            observ: observ || undefined,
            infoPgtos: [infoPgto],
          }],
        }],
      });
      onCriado(r);
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(8,8,10,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
    }} onClick={onFechar}>
      <div className="card" style={{
        width: 720, maxHeight: '92vh', overflow: 'auto', padding: 20,
      }} onClick={e => e.stopPropagation()}>
        <div className="flex mb-2">
          <h2 style={{ margin: 0, fontSize: 16 }}>Novo R-4020 manual</h2>
          <div className="toolbar__spacer" style={{ flex: 1 }} />
          <button onClick={onFechar}>Fechar</button>
        </div>

        {erro && <Alert tipo="danger">{erro}</Alert>}

        <h3 style={{ fontSize: 12, marginTop: 16, color: 'var(--accent)' }}>Identificação</h3>
        <div className="flex" style={{ gap: 12, marginTop: 8 }}>
          <div style={{ flex: '0 0 160px' }}>
            <label className="small muted">Período (AAAA-MM)</label>
            <input type="text" value={perApur} onChange={e => setPerApur(e.target.value)} placeholder="2026-05" />
          </div>
          <div style={{ flex: 1 }}>
            <label className="small muted">CNPJ do estabelecimento</label>
            <input type="text" value={estabNrInsc} onChange={e => setEstabNrInsc(e.target.value)} />
          </div>
        </div>

        <h3 style={{ fontSize: 12, marginTop: 20, color: 'var(--accent)' }}>Beneficiário (PJ)</h3>
        <div className="flex" style={{ gap: 12, marginTop: 8 }}>
          <div style={{ flex: '0 0 220px' }}>
            <label className="small muted">CNPJ do beneficiário</label>
            <input type="text" value={cnpjBenef} onChange={e => setCnpjBenef(e.target.value)} placeholder="00000000000000" />
          </div>
          <div style={{ flex: 1 }}>
            <label className="small muted">Nome do beneficiário</label>
            <input type="text" value={nmBenef} onChange={e => setNmBenef(e.target.value)} />
          </div>
        </div>

        <h3 style={{ fontSize: 12, marginTop: 20, color: 'var(--accent)' }}>Pagamento</h3>
        <div style={{ marginTop: 8 }}>
          <label className="small muted">Natureza de rendimento</label>
          <input
            type="text" placeholder="Buscar natureza (código ou descrição)…"
            value={filtroNat} onChange={e => setFiltroNat(e.target.value)}
            style={{ marginBottom: 4 }}
          />
          <select value={natRend} onChange={e => setNatRend(e.target.value)}>
            <option value="">— Selecione —</option>
            {naturezasFiltradas.map(n => (
              <option key={n.cod} value={n.cod}>
                {n.cod} — {n.descricao}
              </option>
            ))}
          </select>
          {naturezaSelecionada && (
            <div className="small muted" style={{ marginTop: 4 }}>
              Tributos aplicáveis: {(naturezaSelecionada.tributos || []).join(', ') || 'nenhum'}
            </div>
          )}
        </div>

        <div className="flex" style={{ gap: 12, marginTop: 12 }}>
          <div style={{ flex: '0 0 160px' }}>
            <label className="small muted">Data fato gerador</label>
            <input type="date" value={dtFg} onChange={e => setDtFg(e.target.value)} />
          </div>
          <div style={{ flex: '0 0 200px' }}>
            <label className="small muted">Valor bruto</label>
            <input
              type="number" step="0.01" min="0"
              value={vlrBruto} onChange={e => setVlrBruto(e.target.value)}
              style={{ textAlign: 'right' }}
            />
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={calcularAutomatico}
              disabled={calculando || !natRend || !vlrBruto}
            >
              {calculando ? '…' : '⚡ Calcular retenções'}
            </button>
          </div>
        </div>

        <h3 style={{ fontSize: 12, marginTop: 20, color: 'var(--accent)' }}>Retenções</h3>
        <table className="data" style={{ fontSize: 12, marginTop: 8 }}>
          <tbody>
            {[
              ['vlr_ir',     'IR (1708)'],
              ['vlr_csll',   'CSLL'],
              ['vlr_cofins', 'COFINS'],
              ['vlr_pp',     'PIS/PASEP'],
              ['vlr_agreg',  'Agregado'],
            ].map(([campo, label]) => (
              <tr key={campo}>
                <td style={{ width: 200 }}>{label}</td>
                <td>
                  <input
                    type="number" step="0.01" min="0"
                    value={retencoes[campo]}
                    onChange={e => setRetencoes({ ...retencoes, [campo]: e.target.value })}
                    style={{ textAlign: 'right' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: 12 }}>
          <label className="small muted">Observação</label>
          <textarea
            rows={2}
            value={observ}
            onChange={e => setObserv(e.target.value)}
            maxLength={200}
            placeholder="opcional"
          />
        </div>

        <div className="flex mt-2" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onFechar} disabled={salvando}>Cancelar</button>
          <button className="primary" onClick={salvar} disabled={salvando}>
            {salvando ? 'Salvando…' : 'Criar R-4020 (rascunho)'}
          </button>
        </div>
      </div>
    </div>
  );
}
